import { message } from 'antd';
import { isAxiosError } from 'axios';
import { debounce, put, type Effect, type ForkEffect, takeEvery, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '~/api';
import { getWithParamString } from '~/helpers';

import { Columns } from '~/components/Sms/Columns';

import type { Sms } from '~/types/models';
import { type ErrorResponse, type IndexResponse, isErrorResponse } from '~/types/responses';
import type { IRestIndexParams } from '~/types/states';

import { actions as app } from '~/redux/app/slice';
import { watchHandleError } from '~/redux/app/saga';

import { actions } from './slice';
import { getItem, getParams } from './selectors';

const call: any = Effects.call;

export function* watchOptions(): Generator<Effect, void, { rateLimited: boolean; }> {
  const response = yield call(Api.options<{
    rateLimited: boolean;
  }>, '/sms');
  yield put(actions.setRateLimited(!!response.rateLimited))
}

export function* watchDestroy({ payload: { id } }: PayloadAction<Sms>): Generator<Effect, void, void> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));
  try {
    yield call(Api.destroy, '/sms', id);
    message.success('Gabarit de courriels supprimé avec succès.');

    yield call(watchIndex, {});
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchIndex({
  payload: params,
}: PayloadAction<IRestIndexParams<Sms>>): Generator<Effect, void, IndexResponse<Sms>> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    const currentParams = yield select(getParams);
    const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/sms', {
      ...currentParams,
      ...params,
    });
    yield put(actions.setPage(currentPage));
    yield put(actions.setData(items));
    yield put(actions.setPageSize(pageSize));
    yield put(actions.setTotal(total));
    yield put(actions.setTotalPages(totalPages));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchRetrieve({
  payload: { id, params },
}: PayloadAction<{ id: Sms['id'] | Sms['humanId']; params?: IRestIndexParams<Sms> }>): Generator<Effect, void, Sms> {
  try {
    yield put(actions.setLoading(true));
    yield put(actions.setError(null));

    let item = yield select(getItem);
    if (item?.humanId !== id) {
      yield put(app.title('Chargement...'));
      yield put(actions.setItem(null));
      yield put(actions.setInitialItem(null));

      item = yield call(Api.retrieve, `/sms/${id}`, {
        ...(params ?? {}),
        with: getWithParamString(Columns, 'single'),
      });
      yield put(actions.setItem(item));
      yield put(actions.setInitialItem(JSON.stringify(item)));
    }

    yield put(app.title(item.toNumber));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSetParams({
  payload: params,
}: PayloadAction<IRestIndexParams<Sms>>): Generator<Effect, void, Sms> {
  const { sortBy, page, pageSize, ...filters } = params;

  yield put(actions.setPage(page));
  yield put(actions.setPageSize(pageSize));
  yield put(actions.setSortBy(sortBy));
  yield put(actions.setFilters(filters));
}

export function* watchSmsSagas(): Generator<ForkEffect, void> {
  yield debounce(500, actions.index, watchIndex);
  yield debounce(100, actions.retrieve, watchRetrieve);
  yield takeEvery(actions.destroy, watchDestroy);
  yield takeEvery(actions.setParams, watchSetParams);
  yield takeEvery(actions.options, watchOptions);
}

const smsSagas = watchSmsSagas;

export default smsSagas;
